import React, { forwardRef, PropsWithChildren, useEffect } from 'react';
import { mdiChevronDown } from '@mdi/js';
import { Icon } from '@mdi/react';
import clsx from 'clsx';
import { useVisibilityState } from '../../hooks/useVisibilityState';

export function Accordion({
	title,
	open = false,
	duration = 0.3,
	dismount,
	onOpenChange,
	children,
}: PropsWithChildren<{
	title: string,
	open?: boolean,
	duration?: number,
	dismount?: boolean,
	onOpenChange?: (open: boolean) => void,
}>) {
	const [isOpen, setIsOpen, shouldRender] = useVisibilityState(open ?? false, duration * 1000);

	useEffect(() => {
		setIsOpen(open);
	}, [open]);

	const handleClick = () => {
		const next = !isOpen;
		setIsOpen(next);
		onOpenChange?.(next);
	};

	return (
		<div className="flex flex-col gap-4">
			<button
				className="ml-4 flex w-auto self-start items-center gap-2 rounded-sm px-2 py-1 hover:bg-bg-interactive-light-bg-active_hover"
				onClick={handleClick}
				disabled={React.Children.count(children) === 0}
			>
				<span className="text-base font-medium">{title}</span>
				<Icon path={mdiChevronDown} className={clsx('size-6 transition-transform duration-200', isOpen && '-rotate-180')} />
			</button>
			<Drawer
				open={isOpen}
				duration={duration}
				dismount={dismount}
			>
				{children}
			</Drawer>
		</div>
	);
}

type DrawerProps = {
	open: boolean;
	duration?: number;
	dismount?: boolean;
	className?: string;
};

export const Drawer = forwardRef<HTMLDivElement, PropsWithChildren<DrawerProps>>(({
	open,
	duration = 0.3,
	dismount = false,
	className,
	children,
}, ref) => {
	const [isOpen, setIsOpen, shouldRender, transitioning] = useVisibilityState(open, duration * 1000);

	useEffect(() => {
		setIsOpen(open);
	}, [open, setIsOpen]);

	return (
		<div
			ref={ref}
			className={clsx(
				'transition-[height,opacity]',
				isOpen ? 'h-[calc-size(auto,size)] opacity-100' : 'h-0 opacity-0 overflow-hidden',
				transitioning && 'overflow-hidden',
				!shouldRender && '*:collapse',
				className,
			)}
			style={{
				transitionDuration: `${duration}s`,
			}}
		>
			{!(dismount && !shouldRender) && children}
		</div>
	);
});